import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTasks } from '../hooks/useTasks'
import { format, isToday, isTomorrow, isPast } from 'date-fns'
import { HiFilter, HiSortAscending, HiCheckCircle, HiClock, HiFlag } from 'react-icons/hi'

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
  { value: 'blocked', label: 'Blocked' },
]

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const SORT_OPTIONS = [
  { value: 'due_date', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'created_at', label: 'Created' },
  { value: 'title', label: 'Title' },
]

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 }

function TaskRow({ task }) {
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done'
  
  const statusColors = {
    todo: 'bg-status-todo',
    in_progress: 'bg-status-in-progress',
    review: 'bg-status-review',
    done: 'bg-status-done',
    blocked: 'bg-status-blocked',
  }

  const priorityColors = {
    critical: 'text-priority-critical',
    high: 'text-priority-high',
    medium: 'text-priority-medium',
    low: 'text-priority-low',
  }

  return (
    <Link
      to={`/tasks/${task.id}`}
      className="flex items-center gap-4 p-4 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg hover:border-brand-300 transition-colors"
    >
      {/* Status Indicator */}
      <span className={`w-3 h-3 rounded-full shrink-0 ${statusColors[task.status] || 'bg-surface-400'}`} />

      {/* Task Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-surface-800 dark:text-surface-200 truncate">{task.title}</h3>
        <div className="flex items-center gap-3 mt-1 text-sm text-surface-500 dark:text-surface-400">
          {task.project && (
            <span className="truncate">{task.project.name}</span>
          )}
        </div>
      </div>

      {/* Priority */}
      {task.priority && (
        <span className={`flex items-center gap-1 text-sm ${priorityColors[task.priority]}`}>
          <HiFlag className="w-4 h-4" />
          <span className="hidden sm:inline capitalize">{task.priority}</span>
        </span>
      )}

      {/* Due Date */}
      {task.due_date && (
        <span className={`flex items-center gap-1 text-sm shrink-0 ${
          isOverdue ? 'text-red-600' : 'text-surface-500 dark:text-surface-400'
        }`}>
          <HiClock className="w-4 h-4" />
          {isToday(new Date(task.due_date)) ? 'Today' :
           isTomorrow(new Date(task.due_date)) ? 'Tomorrow' :
           format(new Date(task.due_date), 'MMM d')}
        </span>
      )}

      {/* Done Indicator */}
      {task.status === 'done' && (
        <HiCheckCircle className="w-5 h-5 text-green-500 shrink-0" />
      )}
    </Link>
  )
}

export default function MyTasks() {
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [sortBy, setSortBy] = useState('due_date')
  
  const { tasks, loading, error, refresh } = useTasks({ 
    myTasks: true,
    status: statusFilter || undefined,
  })

  // Apply client-side filtering and sorting
  let filteredTasks = [...tasks]

  if (priorityFilter) {
    filteredTasks = filteredTasks.filter(t => t.priority === priorityFilter)
  }

  // Sort
  filteredTasks.sort((a, b) => {
    switch (sortBy) {
      case 'due_date':
        if (!a.due_date && !b.due_date) return 0
        if (!a.due_date) return 1
        if (!b.due_date) return -1
        return new Date(a.due_date) - new Date(b.due_date)
      case 'priority':
        return (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4)
      case 'created_at':
        return new Date(b.created_at) - new Date(a.created_at)
      case 'title':
        return a.title.localeCompare(b.title)
      default:
        return 0
    }
  })

  const overdueTasks = filteredTasks.filter(t => 
    t.due_date && isPast(new Date(t.due_date)) && t.status !== 'done'
  )
  const upcomingTasks = filteredTasks.filter(t => 
    !t.due_date || !isPast(new Date(t.due_date)) || t.status === 'done'
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">My Tasks</h1>
        </div>
        <div className="text-center py-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Failed to load tasks: {error}
          </div>
          <div>
            <button onClick={refresh} className="btn btn-primary btn-sm">
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-200 pb-5 dark:border-surface-700">
        <div>
          <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">My Tasks</h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} assigned to you
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-surface-200 bg-white p-3 shadow-sm dark:border-surface-700 dark:bg-surface-800">
        <div className="flex items-center gap-2">
          <HiFilter className="w-5 h-5 text-surface-500 dark:text-surface-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input py-1.5"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="input py-1.5"
          >
            {PRIORITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <HiSortAscending className="w-5 h-5 text-surface-500 dark:text-surface-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input py-1.5"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Overdue Section */}
      {overdueTasks.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            Overdue ({overdueTasks.length})
          </h2>
          <div className="space-y-2">
            {overdueTasks.map(task => (
              <TaskRow key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}

      {/* All Tasks */}
      <div>
        {overdueTasks.length > 0 && (
          <h2 className="text-sm font-semibold text-surface-600 dark:text-surface-400 uppercase tracking-wider mb-3">
            All Tasks ({upcomingTasks.length})
          </h2>
        )}
        {upcomingTasks.length > 0 ? (
          <div className="space-y-2">
            {upcomingTasks.map(task => (
              <TaskRow key={task.id} task={task} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-surface-500 dark:text-surface-400">No tasks match your filters</p>
          </div>
        )}
      </div>
    </div>
  )
}
